/**
 * Formulário da despesa (ADR 0049, PR-3b2): estado da tela, entrada da API e o que falta para
 * concluir. Puro (sem React), para testar; os cálculos dos meses e do rateio são os mesmos do
 * servidor (@loot/shared), então a prévia mostra exatamente o que será gravado.
 */
import {
  allocateMonthWeighted,
  expenseMonths,
  quantityInMonth,
  fromScaled,
  percentTotal,
  toScaled,
  type FpaExpenseBasis,
  type FpaExpenseRecurrence,
  type FpaFreightPurpose,
} from '@loot/shared';

export interface TargetForm {
  /** Identificador da linha na tela. */
  key: string;
  legalEntityId: string;
  costCenterId: string;
  percent: string;
  /** Rateio por quantidade (PR-4): a quantidade e, se mudar, a partir de qual mês (AAAA-MM). */
  quantity: string;
  changeFrom: string;
  changeQuantity: string;
  /** Valor fixo por mês: o destino recebe primeiro, sem peso. */
  fixed: boolean;
  fixedAmount: string;
  /** Classificação própria deste centro, diferente da do lançamento (com item, vira exceção). */
  override: boolean;
  expenseTypeId: string;
  classificationId: string;
  exceptionNote: string;
}

export interface ExpenseForm {
  name: string;
  /** Atalho do Catálogo (opcional, PR-8e): preenche o Tipo de Gasto e a Classificação. */
  spendItemId: string;
  /** Classificação oficial de Dados Mestres (obrigatória): vale para todos os centros. */
  expenseTypeId: string;
  classificationId: string;
  /** Justificativa quando o par foge do item escolhido (exceção para o FP&A). */
  exceptionNote: string;
  contractingEntityId: string;
  currency: string;
  /** Fornecedor oficial (cadastro de parceiros) ou previsto em texto livre. */
  supplierMode: 'none' | 'official' | 'planned';
  partnerId: string;
  partnerName: string;
  supplierName: string;
  justification: string;
  /** Finalidade do frete e gastos de aquisição (ADR 0032, 13). */
  freightPurpose: string;
  acquisitionStatus: 'unclassified' | 'included' | 'excluded';
  acquisitionPercent: string;
  acquisitionBasis: string;
  basis: FpaExpenseBasis;
  unitAmount: string;
  quantity: string;
  amount: string;
  recurrence: FpaExpenseRecurrence;
  /** AAAA-MM */
  startMonth: string;
  endMonth: string;
  adjustmentPercent: string;
  adjustmentFrom: string;
  /** Personalizada: valor de cada mês (AAAA-MM-01). */
  customMonths: Record<string, string>;
  /** Critério do rateio: percentual ou quantidade (o valor fixo vale em qualquer um). */
  allocationMethod: 'percent' | 'quantity';
  /** De onde vieram as quantidades: digitadas ou contagem de colaboradores (PR-5), e o mês. */
  quantitySource: 'manual' | 'employees';
  quantityCountedOn: string;
  targets: TargetForm[];
}

export interface ItemOption {
  id: string;
  expenseTypeId: string;
  classificationId: string;
}

let seq = 0;
export const newTargetKey = () => `t${++seq}`;

export const blankTarget = (legalEntityId = '', costCenterId = '', percent = ''): TargetForm => ({
  key: newTargetKey(),
  legalEntityId,
  costCenterId,
  percent,
  quantity: '',
  changeFrom: '',
  changeQuantity: '',
  fixed: false,
  fixedAmount: '',
  override: false,
  expenseTypeId: '',
  classificationId: '',
  exceptionNote: '',
});

export function blankForm(year: number): ExpenseForm {
  return {
    name: '',
    spendItemId: '',
    expenseTypeId: '',
    classificationId: '',
    exceptionNote: '',
    contractingEntityId: '',
    currency: '',
    supplierMode: 'none',
    partnerId: '',
    partnerName: '',
    supplierName: '',
    justification: '',
    freightPurpose: '',
    acquisitionStatus: 'unclassified',
    acquisitionPercent: '',
    acquisitionBasis: '',
    basis: 'occurrence',
    unitAmount: '',
    quantity: '',
    amount: '',
    recurrence: 'monthly',
    startMonth: `${year}-01`,
    endMonth: '',
    adjustmentPercent: '',
    adjustmentFrom: '',
    customMonths: {},
    allocationMethod: 'percent',
    quantitySource: 'manual',
    quantityCountedOn: '',
    targets: [],
  };
}

const DECIMAL = /^-?\d{1,15}(\.\d{1,4})?$/;
/** Número digitado no jeito brasileiro ou no inglês ("1.234,56", "1234.56") em texto decimal. */
export function normalizeDecimal(raw: string): string {
  let v = raw.trim().replace(/\s|R\$|US\$|\$|€|£/g, '');
  if (v.includes(',')) v = v.replace(/\./g, '').replace(',', '.');
  return v;
}
export const isDecimal = (raw: string) => DECIMAL.test(normalizeDecimal(raw));
const dec = (raw: string) => (raw.trim() === '' ? null : normalizeDecimal(raw));
const month = (m: string) => (m ? `${m.slice(0, 7)}-01` : null);

/** Tipo de Gasto e Classificação do centro: os do lançamento, salvo se a pessoa trocou. */
export function targetClasses(t: TargetForm, f: ExpenseForm) {
  return t.override
    ? { expenseTypeId: t.expenseTypeId, classificationId: t.classificationId }
    : { expenseTypeId: f.expenseTypeId, classificationId: f.classificationId };
}

/** O par foge do item escolhido? Sem item, nunca (o par oficial vale por si). */
export function diverges(
  pair: { expenseTypeId: string; classificationId: string },
  item: ItemOption | undefined,
): boolean {
  return (
    !!item &&
    !!pair.expenseTypeId &&
    (pair.expenseTypeId !== item.expenseTypeId || pair.classificationId !== item.classificationId)
  );
}

/** Justificativa de um centro: a dele, se trocou a classificação; senão, a do lançamento. */
function targetNote(f: ExpenseForm, t: TargetForm, item: ItemOption | undefined) {
  const pair = targetClasses(t, f);
  if (!diverges(pair, item)) return null;
  return (t.override ? t.exceptionNote : f.exceptionNote).trim() || null;
}

/** Entrada completa (Concluir), no formato da API. */
export function toInput(f: ExpenseForm, item: ItemOption | undefined) {
  return {
    name: f.name.trim(),
    freightPurpose: (f.freightPurpose || null) as FpaFreightPurpose | null,
    acquisitionStatus: f.acquisitionStatus,
    acquisitionPercent: f.acquisitionStatus === 'included' ? dec(f.acquisitionPercent) : null,
    acquisitionBasis: f.acquisitionStatus === 'included' ? f.acquisitionBasis.trim() || null : null,
    spendItemId: f.spendItemId || null,
    partnerId: f.supplierMode === 'official' ? f.partnerId || null : null,
    supplierName: f.supplierMode === 'planned' ? f.supplierName.trim() || null : null,
    contractingEntityId: f.contractingEntityId,
    currency: f.currency,
    basis: f.basis,
    unitAmount: f.basis === 'unit' ? dec(f.unitAmount) : null,
    quantity: f.basis === 'unit' ? dec(f.quantity) : null,
    amount: f.basis === 'unit' || f.recurrence === 'custom' ? null : dec(f.amount),
    recurrence: f.recurrence,
    startMonth: f.startMonth,
    endMonth: f.recurrence === 'once' || f.recurrence === 'custom' ? null : f.endMonth || null,
    adjustmentPercent: f.recurrence === 'custom' ? null : dec(f.adjustmentPercent),
    adjustmentFrom:
      f.recurrence === 'custom' || !f.adjustmentPercent ? null : f.adjustmentFrom || null,
    customMonths:
      f.recurrence === 'custom'
        ? Object.entries(f.customMonths)
            .filter(([, v]) => v.trim() !== '' && isDecimal(v))
            .map(([m, v]) => ({ month: m, amount: normalizeDecimal(v) }))
        : [],
    justification: f.justification.trim() || null,
    allocationMethod: f.allocationMethod,
    quantitySource: f.allocationMethod === 'quantity' ? f.quantitySource : ('manual' as const),
    quantityCountedOn:
      f.allocationMethod === 'quantity' && f.quantitySource === 'employees'
        ? month(f.quantityCountedOn)
        : null,
    targets: f.targets.map((t) => ({
      legalEntityId: t.legalEntityId,
      costCenterId: t.costCenterId,
      ...targetWeight(f, t),
      ...targetClasses(t, f),
      exceptionNote: targetNote(f, t, item),
    })),
  };
}

/** Peso do destino na entrada da API: valor fixo, percentual ou quantidade (com a mudança). */
function targetWeight(f: ExpenseForm, t: TargetForm) {
  if (t.fixed) {
    return { percent: null, quantity: null, quantityChanges: [], fixedAmount: dec(t.fixedAmount) };
  }
  if (f.allocationMethod === 'quantity') {
    const change =
      t.changeFrom && t.changeQuantity.trim() && isDecimal(t.changeQuantity)
        ? [{ from: month(t.changeFrom)!, quantity: normalizeDecimal(t.changeQuantity) }]
        : [];
    return { percent: null, quantity: dec(t.quantity), quantityChanges: change, fixedAmount: null };
  }
  return { percent: dec(t.percent), quantity: null, quantityChanges: [], fixedAmount: null };
}

/** Rascunho: o formulário como está, sem exigir nada além do nome. */
export function toDraft(f: ExpenseForm, item: ItemOption | undefined) {
  const full = toInput(f, item);
  const clean = <T extends Record<string, unknown>>(o: T) =>
    Object.fromEntries(Object.entries(o).filter(([, v]) => v !== null && v !== '')) as Partial<T>;
  return {
    name: f.name.trim() || '(sem nome)',
    draft: {
      ...clean({
        spendItemId: full.spendItemId,
        expenseTypeId: f.expenseTypeId,
        classificationId: f.classificationId,
        exceptionNote: f.exceptionNote.trim(),
        partnerId: full.partnerId,
        supplierName: full.supplierName,
        contractingEntityId: full.contractingEntityId,
        currency: full.currency,
        basis: full.basis,
        unitAmount: f.basis === 'unit' && isDecimal(f.unitAmount) ? full.unitAmount : null,
        quantity: f.basis === 'unit' && isDecimal(f.quantity) ? full.quantity : null,
        amount: full.amount && isDecimal(f.amount) ? full.amount : null,
        recurrence: full.recurrence,
        startMonth: full.startMonth,
        endMonth: full.endMonth,
        adjustmentPercent:
          full.adjustmentPercent && isDecimal(f.adjustmentPercent) ? full.adjustmentPercent : null,
        adjustmentFrom: full.adjustmentFrom,
        justification: full.justification,
        freightPurpose: full.freightPurpose,
        // Não classificado é o padrão: o rascunho só guarda o que foi escolhido.
        acquisitionStatus:
          full.acquisitionStatus === 'unclassified' ? null : full.acquisitionStatus,
        acquisitionPercent: full.acquisitionPercent,
        acquisitionBasis: full.acquisitionBasis,
      }),
      allocationMethod: f.allocationMethod,
      ...(full.quantityCountedOn
        ? { quantitySource: full.quantitySource, quantityCountedOn: full.quantityCountedOn }
        : {}),
      customMonths: full.customMonths,
      targets: f.targets.map((t) =>
        clean({
          legalEntityId: t.legalEntityId,
          costCenterId: t.costCenterId,
          percent: !t.fixed && t.percent.trim() ? normalizeDecimal(t.percent) : null,
          quantity: !t.fixed && t.quantity.trim() ? normalizeDecimal(t.quantity) : null,
          quantityChanges:
            !t.fixed && t.changeFrom && t.changeQuantity.trim()
              ? [{ from: month(t.changeFrom), quantity: normalizeDecimal(t.changeQuantity) }]
              : undefined,
          fixedAmount: t.fixed && t.fixedAmount.trim() ? normalizeDecimal(t.fixedAmount) : null,
          ...(t.override
            ? {
                expenseTypeId: t.expenseTypeId,
                classificationId: t.classificationId,
                exceptionNote: t.exceptionNote.trim() || null,
              }
            : targetClasses(t, f)),
        }),
      ),
    },
  };
}

/** Formulário a partir do rascunho guardado (o que faltar fica em branco). */
export function fromDraft(
  name: string,
  d: Record<string, unknown>,
  year: number,
  item?: (id: string) => ItemOption | undefined,
): ExpenseForm {
  const f = blankForm(year);
  const s = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '');
  const targets = Array.isArray(d.targets) ? (d.targets as Record<string, string>[]) : [];
  const months = Array.isArray(d.customMonths)
    ? (d.customMonths as { month: string; amount: string }[])
    : [];
  const it = item?.(s('spendItemId'));
  // O par do lançamento: o guardado; nos rascunhos antigos, o do item ou o do 1º centro.
  const first = targets.find((t) => t.expenseTypeId && t.classificationId);
  const expenseTypeId = s('expenseTypeId') || it?.expenseTypeId || first?.expenseTypeId || '';
  const classificationId =
    s('classificationId') || it?.classificationId || first?.classificationId || '';
  return {
    ...f,
    name,
    spendItemId: s('spendItemId'),
    expenseTypeId,
    classificationId,
    exceptionNote: s('exceptionNote'),
    contractingEntityId: s('contractingEntityId'),
    currency: s('currency'),
    supplierMode: s('partnerId') ? 'official' : s('supplierName') ? 'planned' : 'none',
    partnerId: s('partnerId'),
    supplierName: s('supplierName'),
    justification: s('justification'),
    freightPurpose: s('freightPurpose'),
    acquisitionStatus: (s('acquisitionStatus') ||
      'unclassified') as ExpenseForm['acquisitionStatus'],
    acquisitionPercent: s('acquisitionPercent'),
    acquisitionBasis: s('acquisitionBasis'),
    basis: (s('basis') || 'occurrence') as FpaExpenseBasis,
    unitAmount: s('unitAmount'),
    quantity: s('quantity'),
    amount: s('amount'),
    recurrence: (s('recurrence') || 'monthly') as FpaExpenseRecurrence,
    startMonth: s('startMonth').slice(0, 7) || f.startMonth,
    endMonth: s('endMonth').slice(0, 7),
    adjustmentPercent: s('adjustmentPercent'),
    adjustmentFrom: s('adjustmentFrom').slice(0, 7),
    customMonths: Object.fromEntries(months.map((m) => [`${m.month.slice(0, 7)}-01`, m.amount])),
    allocationMethod: s('allocationMethod') === 'quantity' ? 'quantity' : 'percent',
    quantitySource: s('quantitySource') === 'employees' ? 'employees' : 'manual',
    quantityCountedOn: s('quantityCountedOn').slice(0, 7),
    targets: targets.map((t) => {
      const override =
        !!t.expenseTypeId &&
        (t.expenseTypeId !== expenseTypeId || t.classificationId !== classificationId);
      return {
        ...blankTarget(t.legalEntityId ?? '', t.costCenterId ?? '', t.percent ?? ''),
        ...weightFields(t),
        override,
        expenseTypeId: override ? (t.expenseTypeId ?? '') : '',
        classificationId: override ? (t.classificationId ?? '') : '',
        exceptionNote: t.exceptionNote ?? '',
      };
    }),
  };
}

/** Campos de peso de um destino guardado (rascunho ou despesa): quantidade, mudança e valor fixo. */
export function weightFields(t: {
  quantity?: unknown;
  quantityChanges?: unknown;
  fixedAmount?: unknown;
}) {
  const changes = Array.isArray(t.quantityChanges)
    ? (t.quantityChanges as { from: string; quantity: string }[])
    : [];
  const fixedAmount = typeof t.fixedAmount === 'string' ? t.fixedAmount : '';
  return {
    quantity: typeof t.quantity === 'string' ? t.quantity : '',
    changeFrom: changes[0]?.from.slice(0, 7) ?? '',
    changeQuantity: changes[0]?.quantity ?? '',
    fixed: fixedAmount !== '',
    fixedAmount,
  };
}

/** "Dividir igualmente": percentuais com até 4 casas que somam exatamente 100%. */
export function equalSplit(n: number): string[] {
  if (n <= 0) return [];
  const total = 1_000_000n; // 100,0000%
  const each = total / BigInt(n);
  const out = Array.from({ length: n }, () => each);
  out[n - 1] = out[n - 1]! + (total - each * BigInt(n));
  return out.map((v) => trimPercent(fromScaled(v)));
}

/** "Completar 100% no último": o último recebe o que falta. */
export function completeLast(percents: string[]): string[] {
  if (percents.length === 0) return percents;
  const head = percents
    .slice(0, -1)
    .map((p) => (isDecimal(p) ? toScaled(normalizeDecimal(p)) : 0n));
  const rest = 1_000_000n - head.reduce((s, x) => s + x, 0n);
  return [...percents.slice(0, -1), rest > 0n ? trimPercent(fromScaled(rest)) : '0'];
}

const trimPercent = (v: string) => v.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

/** Soma dos percentuais digitados, em texto ("90" ou "33.3333"). */
export function percentSum(targets: TargetForm[]): string {
  const valid = targets
    .filter((t) => !t.fixed && isDecimal(t.percent))
    .map((t) => ({ percent: normalizeDecimal(t.percent) }));
  return trimPercent(fromScaled(percentTotal(valid)));
}

export type Missing =
  | 'name'
  | 'expenseType'
  | 'classification'
  | 'entity'
  | 'currency'
  | 'value'
  | 'quantity'
  | 'startMonth'
  | 'customMonths'
  | 'adjustment'
  | 'targets'
  | 'targetCenter'
  | 'targetPercent'
  | 'targetQuantity'
  | 'targetFixed'
  | 'percentSum'
  | 'exceptionNote'
  | 'targetExceptionNote';

/** O que falta para concluir (vazio = pode concluir). O servidor confere de novo. */
export function missingForConclude(f: ExpenseForm, item?: ItemOption): Missing[] {
  const out: Missing[] = [];
  if (!f.name.trim()) out.push('name');
  if (!f.expenseTypeId) out.push('expenseType');
  if (!f.classificationId) out.push('classification');
  // Fugiu do item escolhido: justificativa (exceção para o FP&A, regra de conferência mantida).
  if (diverges(f, item) && f.exceptionNote.trim().length < 3) out.push('exceptionNote');
  if (!f.contractingEntityId) out.push('entity');
  if (!f.currency) out.push('currency');
  if (f.recurrence === 'custom') {
    if (!Object.values(f.customMonths).some((v) => v.trim() && isDecimal(v)))
      out.push('customMonths');
  } else if (f.basis === 'unit') {
    if (!isDecimal(f.unitAmount)) out.push('value');
    if (!isDecimal(f.quantity)) out.push('quantity');
  } else if (!isDecimal(f.amount)) out.push('value');
  if (!f.startMonth) out.push('startMonth');
  if (f.recurrence !== 'custom' && !!f.adjustmentPercent.trim() !== !!f.adjustmentFrom)
    out.push('adjustment');
  if (f.targets.length === 0) out.push('targets');
  if (f.targets.some((t) => !t.legalEntityId || !t.costCenterId)) out.push('targetCenter');
  // Rateio (PR-4): destino com valor fixo precisa do valor; os demais, do peso do critério.
  const weighted = f.targets.filter((t) => !t.fixed);
  if (f.targets.some((t) => t.fixed && !isDecimal(t.fixedAmount))) out.push('targetFixed');
  if (f.allocationMethod === 'quantity') {
    if (
      weighted.some((t) => !isDecimal(t.quantity) || toScaled(normalizeDecimal(t.quantity)) <= 0n)
    )
      out.push('targetQuantity');
  } else if (weighted.some((t) => !isDecimal(t.percent))) out.push('targetPercent');
  else if (weighted.length > 0 && percentSum(f.targets) !== '100') out.push('percentSum');
  if (
    f.targets.some(
      (t) => t.override && diverges(targetClasses(t, f), item) && t.exceptionNote.trim().length < 3,
    )
  )
    out.push('targetExceptionNote');
  return out;
}

/** Prévia dos 12 meses (a mesma conta do servidor). Nula se ainda não dá para calcular. */
export function previewMonths(f: ExpenseForm, year: number, minorUnits: number) {
  try {
    const input = toInput(f, undefined);
    return expenseMonths(
      {
        recurrence: input.recurrence,
        basis: input.basis,
        unitAmount: input.unitAmount,
        quantity: input.quantity,
        amount: input.amount,
        startMonth: month(input.startMonth) ?? `${year}-01-01`,
        endMonth: month(input.endMonth ?? ''),
        adjustmentPercent: input.adjustmentPercent,
        adjustmentFrom: month(input.adjustmentFrom ?? ''),
        customMonths: input.customMonths,
      },
      year,
      minorUnits,
    );
  } catch {
    return null;
  }
}

/** Prévia do rateio: parcela de cada linha em cada mês (só com 100% e centros escolhidos). */
export function previewAllocation(
  f: ExpenseForm,
  months: { month: string; amount: string }[],
  minorUnits: number,
  codeOf: (costCenterId: string) => string,
): Map<string, Record<string, string>> | null {
  if (f.targets.length === 0 || f.targets.some((t) => !t.costCenterId)) return null;
  const missing = missingForConclude(f).filter((m) =>
    ['targetPercent', 'targetQuantity', 'targetFixed', 'percentSum'].includes(m),
  );
  if (missing.length) return null;
  const input = toInput(f, undefined);
  const out = new Map<string, Record<string, string>>(f.targets.map((t) => [t.key, {}]));
  try {
    for (const m of months) {
      const targets = f.targets.map((t, i) => {
        const w = input.targets[i]!;
        return {
          key: t.key,
          costCenterCode: codeOf(t.costCenterId),
          fixed: w.fixedAmount,
          weight:
            f.allocationMethod === 'quantity'
              ? quantityInMonth(w.quantity, w.quantityChanges, m.month)
              : w.percent,
        };
      });
      for (const p of allocateMonthWeighted(m.amount, minorUnits, targets))
        out.get(p.key)![m.month] = p.amount;
    }
  } catch {
    // Valores fixos acima do valor do mês: a tela avisa e o servidor recusa.
    return null;
  }
  return out;
}

/**
 * A conta da prévia em palavras (PR-3c2): qual fórmula e com quais números. A tela traduz e
 * formata; os valores vêm da própria prévia (a mesma conta do servidor).
 */
export function formulaOf(
  f: ExpenseForm,
  months: { month: string; amount: string }[],
): { key: string; values: Record<string, string | number> } | null {
  if (!months.length) return null;
  const total = fromScaled(months.reduce((s, m) => s + toScaled(m.amount), 0n));
  const count = months.length;
  const base: { key: string; values: Record<string, string | number> } =
    f.recurrence === 'custom'
      ? { key: 'custom', values: { count, total } }
      : f.basis === 'unit'
        ? {
            key: 'unit',
            values: {
              count,
              unit: normalizeDecimal(f.unitAmount),
              quantity: normalizeDecimal(f.quantity),
              total,
            },
          }
        : f.basis === 'period'
          ? { key: 'period', values: { amount: normalizeDecimal(f.amount), count, total } }
          : { key: 'occurrence', values: { count, each: normalizeDecimal(f.amount), total } };
  if (f.recurrence !== 'custom' && f.adjustmentPercent.trim() && f.adjustmentFrom) {
    return {
      key: `${base.key}Adjusted`,
      values: {
        ...base.values,
        percent: normalizeDecimal(f.adjustmentPercent),
        from: `${f.adjustmentFrom.slice(0, 7)}-01`,
      },
    };
  }
  return base;
}

/**
 * Colar do Excel nos meses da recorrência Personalizada: os valores copiados (numa linha ou numa
 * coluna) ocupam o mês em que se colou e os seguintes; o que passa de dezembro é ignorado.
 */
export function pasteMonths(
  current: Record<string, string>,
  months: string[],
  start: string,
  cells: string[][],
): Record<string, string> {
  const values = cells.length === 1 ? cells[0]! : cells.map((r) => r[0] ?? '');
  const from = months.indexOf(start);
  if (from < 0) return current;
  const out = { ...current };
  values.forEach((v, i) => {
    const m = months[from + i];
    if (m) out[m] = v.trim();
  });
  return out;
}
